import { useState, useCallback, useMemo } from 'react'
import { useUser } from '@/contexts/UserContext'
import { usePersistentState, makeFilterKey } from '@/hooks/use-persistent-state'
import { useNavigate } from 'react-router-dom'
import { useOrders, useOrderStats, useUsers, useBulkUpdateOrders } from '@/hooks/use-api'
import { usePagination } from '@/hooks/use-pagination'
import { ownerPickerOptions } from '@/lib/salesReps'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { BulkActionsBar } from '@/components/BulkActionsBar'
import { BulkUpdateModal, type BulkUpdateField } from '@/components/BulkUpdateModal'
import { RefreshButton } from '@/components/RefreshButton'
import { SearchWithRecent } from '@/components/ui/search-with-recent'
import { CategoryBadge } from '@/components/ui/CategoryBadge'
import { ResizableTable } from '@/components/ui/resizable-table'
import { createOrdersColumns, ordersDefaultColumnSizing } from '@/config/columns/orders-columns'
import { ColumnPicker } from '@/components/ui/column-picker'
import { useColumnVisibility } from '@/hooks/use-column-visibility'
import {
  FileText,
  CheckCircle2,
  FileEdit,
  ThumbsUp,
  DollarSign,
  TrendingUp,
  Download,
  Plus,
  ChevronDown,
} from 'lucide-react'
import { formatCurrency, cn } from '@/lib/utils'
import { exportCurrentPage } from '@/utils/csvExport'
import { MobileOrderList } from '@/components/MobileOrderCard'
import { useMobile } from '@/hooks/use-mobile'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { explainError } from '@/lib/explain-error'

const ALL_ORDER_STATUSES = ['Draft', 'Pending', 'Approved', 'Processed', 'Denied']

export default function Orders() {
  const navigate = useNavigate()
  const { currentUser, isAdmin, impersonatedUser } = useUser()
  const { toast } = useToast()
  const fk = makeFilterKey(currentUser?.id, 'orders')
  // Only Admin/Super Admin can bulk-reassign the owner (the sole orders bulk action).
  const canReassignOwner =
    isAdmin && (!impersonatedUser || ['Admin', 'Super Admin'].includes(impersonatedUser.user_type))
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState(false)
  const [search, setSearch] = useState('')
  // Status filter persisted as an array (Sets aren't JSON-serializable);
  // expose the familiar Set API via a derived value + wrapper setter.
  const [statusList, setStatusList] = usePersistentState<string[]>(
    fk('statuses'),
    ALL_ORDER_STATUSES
  )
  const selectedStatuses = useMemo(() => new Set(statusList), [statusList])
  const setSelectedStatuses = useCallback(
    (next: Set<string> | ((prev: Set<string>) => Set<string>)) =>
      setStatusList((prevArr) => {
        const resolved = typeof next === 'function' ? next(new Set(prevArr)) : next
        return Array.from(resolved)
      }),
    [setStatusList]
  )
  const [sortBy, setSortBy] = usePersistentState(fk('sortBy'), 'created_date')
  const [sortOrder, setSortOrder] = usePersistentState<'asc' | 'desc'>(fk('sortOrder'), 'desc')
  const { page, prevPage, nextPage, firstPage, extract } = usePagination()

  const isMobile = useMobile()
  const statusParam =
    selectedStatuses.size < ALL_ORDER_STATUSES.length
      ? Array.from(selectedStatuses).join(',')
      : undefined
  const { data, isLoading } = useOrders({
    search,
    page,
    sort_by: sortBy,
    sort_order: sortOrder,
    ...(statusParam && { status: statusParam }),
  })

  const handleStatusCardClick = (status: string) => {
    if (status === '') {
      // "Total Orders" card — select all
      setSelectedStatuses(new Set(ALL_ORDER_STATUSES))
    } else {
      // Toggle: if this is the only one selected, deselect (show all). Otherwise select only this one.
      if (selectedStatuses.size === 1 && selectedStatuses.has(status)) {
        setSelectedStatuses(new Set(ALL_ORDER_STATUSES))
      } else {
        setSelectedStatuses(new Set([status]))
      }
    }
    firstPage()
  }
  const { data: stats } = useOrderStats()
  const { data: usersData } = useUsers({ limit: 10000 })
  const users = usersData?.data ?? []
  const bulkUpdateMutation = useBulkUpdateOrders()

  const {
    items: orders,
    total,
    currentPage,
    limit,
    totalPages,
    hasNextPage,
    hasPrevPage,
  } = extract(data)

  const handleSort = useCallback(
    (field: string) => {
      if (sortBy === field) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortBy(field)
        setSortOrder('asc')
      }
      firstPage()
    },
    [sortBy, firstPage]
  )

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  const handleToggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.size === orders.length ? new Set() : new Set(orders.map((o) => o.id))
    )
  }

  const handleBulkUpdate = async (updates: Record<string, any>) => {
    try {
      await bulkUpdateMutation.mutateAsync({ ids: Array.from(selectedIds), updates })
      toast({
        title: 'Orders updated',
        description: `${selectedIds.size} order${selectedIds.size === 1 ? '' : 's'} updated successfully.`,
      })
      setBulkUpdateModalOpen(false)
      setSelectedIds(new Set())
    } catch (error: any) {
      toast({
        title: 'Update failed',
        description: explainError(error, 'Could not update the selected orders.'),
        variant: 'destructive',
      })
    }
  }

  const ordersUpdateFields: BulkUpdateField[] = canReassignOwner
    ? [
        {
          key: 'owner_id',
          label: 'Owner',
          type: 'select',
          options: ownerPickerOptions(users).map((u) => ({ value: u.id, label: u.name })),
        },
      ]
    : []

  const columns = useMemo(
    () =>
      createOrdersColumns(
        canReassignOwner
          ? {
              selectedIds,
              onToggleSelect: handleToggleSelect,
              onToggleSelectAll: handleToggleSelectAll,
              allSelected: selectedIds.size === orders.length && orders.length > 0,
            }
          : {}
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canReassignOwner, selectedIds, orders.length]
  )

  const [columnVisibility, setColumnVisibility] = useColumnVisibility('orders-column-visibility')

  const handleExportCSV = () => {
    exportCurrentPage(orders, 'Orders', [
      'order_number',
      'account_name',
      'status',
      'effective_date',
      'total_amount',
      'item_count',
    ])
  }

  return (
    <div className="space-y-4">
      {canReassignOwner && selectedIds.size > 0 && (
        <BulkActionsBar
          selectedCount={selectedIds.size}
          entityType="order"
          onClearSelection={() => setSelectedIds(new Set())}
          onBulkUpdate={() => setBulkUpdateModalOpen(true)}
          isUpdating={bulkUpdateMutation.isPending}
        />
      )}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <CategoryBadge type="order" />
            <h1 className="text-3xl font-bold tracking-tight">Orders</h1>
          </div>
          <p className="text-muted-foreground mt-1">
            Manage customer orders and track order processing
          </p>
        </div>
        <div className="flex gap-2">
          <RefreshButton queryKey={['orders']} label="orders" />
          <Button
            onClick={handleExportCSV}
            variant="outline"
            disabled={orders.length === 0}
            aria-label="Export orders to CSV"
          >
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            Export CSV
          </Button>
          <Button onClick={() => navigate('/orders/new')} aria-label="Create new order">
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            New Order
          </Button>
        </div>
      </div>

      <div className="grid gap-2 md:grid-cols-3 lg:grid-cols-6">
        <Card
          className={cn(
            'cursor-pointer transition-all hover:bg-muted',
            selectedStatuses.size === ALL_ORDER_STATUSES.length && 'ring-2 ring-primary'
          )}
          onClick={() => handleStatusCardClick('')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleStatusCardClick('')}
          aria-label="Show all orders"
          aria-pressed={selectedStatuses.size === ALL_ORDER_STATUSES.length}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
            <FileText className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{stats?.total_count ?? 0}</div>
            <p className="text-xs text-muted-foreground leading-tight">
              Showing {orders.length} on this page
            </p>
          </CardContent>
        </Card>

        <Card
          className={cn(
            'cursor-pointer transition-all hover:bg-muted',
            selectedStatuses.size === 1 &&
              selectedStatuses.has('Processed') &&
              'ring-2 ring-primary'
          )}
          onClick={() => handleStatusCardClick('Processed')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleStatusCardClick('Processed')}
          aria-label="Filter by Processed orders"
          aria-pressed={selectedStatuses.size === 1 && selectedStatuses.has('Processed')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Processed</CardTitle>
            <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{stats?.processed_count ?? 0}</div>
          </CardContent>
        </Card>

        <Card
          className={cn(
            'cursor-pointer transition-all hover:bg-muted',
            selectedStatuses.size === 1 && selectedStatuses.has('Draft') && 'ring-2 ring-primary'
          )}
          onClick={() => handleStatusCardClick('Draft')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleStatusCardClick('Draft')}
          aria-label="Filter by Draft orders"
          aria-pressed={selectedStatuses.size === 1 && selectedStatuses.has('Draft')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Draft</CardTitle>
            <FileEdit className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{stats?.draft_count ?? 0}</div>
          </CardContent>
        </Card>

        <Card
          className={cn(
            'cursor-pointer transition-all hover:bg-muted',
            selectedStatuses.size === 1 && selectedStatuses.has('Approved') && 'ring-2 ring-primary'
          )}
          onClick={() => handleStatusCardClick('Approved')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleStatusCardClick('Approved')}
          aria-label="Filter by Approved orders"
          aria-pressed={selectedStatuses.size === 1 && selectedStatuses.has('Approved')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Approved</CardTitle>
            <ThumbsUp className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{stats?.approved_count ?? 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">
              {formatCurrency(stats?.total_revenue ?? 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Avg Order Value</CardTitle>
            <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">
              {formatCurrency(stats?.average_order_value ?? 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Orders</CardTitle>
          <CardDescription>View and manage customer orders</CardDescription>
          <div className="flex items-center gap-2">
            <SearchWithRecent
              value={search}
              onChange={setSearch}
              placeholder="Search orders..."
              ariaLabel="Search orders"
              entityKey="orders"
            />
            <ColumnPicker
              columns={columns}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="w-[170px] justify-between"
                  aria-label="Filter by order status"
                >
                  {selectedStatuses.size === ALL_ORDER_STATUSES.length
                    ? 'All Statuses'
                    : `Status (${selectedStatuses.size})`}
                  <ChevronDown className="ml-2 h-4 w-4 opacity-50" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {ALL_ORDER_STATUSES.map((status) => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={selectedStatuses.has(status)}
                    onCheckedChange={(checked) => {
                      setSelectedStatuses((prev) => {
                        const next = new Set(prev)
                        checked ? next.add(status) : next.delete(status)
                        return next
                      })
                      firstPage()
                    }}
                  >
                    {status}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuCheckboxItem
                  checked={selectedStatuses.size === ALL_ORDER_STATUSES.length}
                  onCheckedChange={(checked) => {
                    setSelectedStatuses(checked ? new Set(ALL_ORDER_STATUSES) : new Set())
                    firstPage()
                  }}
                >
                  Select All
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading orders...</div>
          ) : orders.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No orders found.</div>
          ) : isMobile ? (
            <MobileOrderList orders={orders} />
          ) : (
            <ResizableTable
              columns={columns}
              data={orders}
              storageKey="orders-column-widths"
              defaultColumnSizing={ordersDefaultColumnSizing}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
            />
          )}

          {/* Pagination */}
          {!isLoading && total > limit && (
            <div className="flex items-center justify-between px-2 py-4">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, total)} of{' '}
                {total} orders
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={prevPage}
                  disabled={!hasPrevPage}
                  aria-label="Go to previous page"
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1 text-sm">
                  Page {currentPage} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={nextPage}
                  disabled={!hasNextPage}
                  aria-label="Go to next page"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <BulkUpdateModal
        open={bulkUpdateModalOpen}
        onOpenChange={setBulkUpdateModalOpen}
        selectedCount={selectedIds.size}
        entityType="order"
        fields={ordersUpdateFields}
        onConfirm={handleBulkUpdate}
        isUpdating={bulkUpdateMutation.isPending}
      />
    </div>
  )
}
